import _ from 'lodash';

export const SCOPE_LIBRARIES = Symbol.for('platform.scopeLibraries');

export function bindFunctionsTo(obj, bindTarget) {
    if (!obj || typeof obj !== 'object')
        return;

    for (const key of Object.keys(obj)) {
        if (typeof obj[key] === 'function')
            obj[key] = obj[key].bind(bindTarget);
    }
}

export function normalizePath(path) {
    if (!path || typeof path !== 'string')
        return '';
    return path.replace(/^\/+|\/+$/g, '');
}

export function cacheKey(customerId, path) {
    return `${customerId}|UserControl|${normalizePath(path)}`;
}

export function loadInProgressKey(cacheKeyValue) {
    return `scopeLibrary:${cacheKeyValue}`;
}

export function parseScopeItemsFromResponse(data) {
    const payload = data?.Result || data;
    if (!payload)
        return null;

    const scopeItems = payload.$$ScopeItems || {};
    return {
        scopeScript: scopeItems.ScopeScript ?? payload.ScopeScript ?? null,
        scopePublic: scopeItems.ScopePublic ?? payload.ScopePublic ?? null,
        scopeIncludes: scopeItems.ScopeIncludes ?? payload.ScopeIncludes ?? [],
    };
}

export function attachScopeLibraries(scopeitems, flatHandles) {
    if (scopeitems && flatHandles && Object.keys(flatHandles).length)
        scopeitems[SCOPE_LIBRARIES] = flatHandles;
}

export function detachScopeLibraries(scopeitems) {
    if (scopeitems && SCOPE_LIBRARIES in scopeitems)
        delete scopeitems[SCOPE_LIBRARIES];
}

export function getScopeLibraries(scopeitems) {
    return scopeitems?.[SCOPE_LIBRARIES] || null;
}

export function getControlScope(scopeitems) {
    return scopeitems;
}

/**
 * True when ScopeScript is missing or is an empty object-literal wrapper with no members.
 * Matches the designer legacy-gate check and runtime ScopeScript vs legacy branching.
 * @param {unknown} scopeScript
 * @returns {boolean}
 */
export function isScopeScriptEmpty(scopeScript) {
    if (scopeScript == null || scopeScript === '')
        return true;

    const trimmed = String(scopeScript).trim();
    if (!trimmed)
        return true;

    const wrapperMatch = /^\(\s*\{([\s\S]*)\}\s*\)$/.exec(trimmed);
    if (!wrapperMatch)
        return false;

    const withoutComments = wrapperMatch[1]
        .replace(/\/\*[\s\S]*?\*\//g, '')
        .replace(/\/\/.*$/gm, '')
        .trim();

    return withoutComments.length === 0;
}

export function compileLibraryFactory(script, includeHandleNames) {
    return new Function(
        ...includeHandleNames,
        'api',
        'care',
        'util',
        '_',
        '"use strict"; return (' + script + ');'
    );
}

export function compileUserControlScopeScript(script, deps) {
    const { Control, Params, api: apiWrapper, care, util, controlModel, libraries } = deps;
    const handleNames = Object.keys(libraries || {});
    const handleValues = Object.values(libraries || {});
    const factory = new Function(
        'Control',
        ...handleNames,
        'Params',
        'api',
        'care',
        'util',
        'controlModel',
        '_',
        '"use strict"; return (' + script + ');'
    );
    const exports = factory(Control, ...handleValues, Params, apiWrapper, care, util, controlModel, _);
    bindFunctionsTo(exports, Control);
    Object.assign(Control, exports);
    return Control;
}

export function createInstantiateLibrary({ load, api, care, util, _ }) {
    return async function instantiateLibrary(cachedEntry, resolvingStack = [], outFlat = null) {
        if (resolvingStack.includes(cachedEntry.path))
            throw new Error(`Scope include circular reference: ${cachedEntry.path}`);

        const peerHandles = {};
        for (const inc of cachedEntry.scopeIncludes || []) {
            if (outFlat && inc.As in outFlat)
                throw new Error(`Duplicate scope include handle: ${inc.As}`);

            const peerCached = await load(inc.Path);
            peerHandles[inc.As] = await instantiateLibrary(
                peerCached,
                [...resolvingStack, cachedEntry.path],
                outFlat
            );

            if (outFlat)
                outFlat[inc.As] = peerHandles[inc.As];
        }

        const handleNames = Object.keys(peerHandles);
        const handleValues = Object.values(peerHandles);
        const libObj = cachedEntry.factory(...handleValues, api, care, util, _);
        bindFunctionsTo(libObj, libObj);
        return libObj;
    };
}
